import { S } from './state.js';
import { Audio } from './audio.js';
import { BAL } from './config.js';
import { ARCH_INFO, ITEMS, TIERS } from './data/items.js';
import { DEV } from './data/progression.js';
import { WEAPONS } from './data/weapons.js';
import { drawAchToasts, drawRelicToasts } from './achievements.js';
import { tryDash } from './allies.js';
import { srcMeta } from './meter.js';
import { player } from './entities.js';
import { ctx } from './canvas.js';
import { JOY_SAT, joy } from './input.js';
import { bloodConfirmOverlay, bossRewardOverlay, levelUpOverlay, pandoraConfirmOverlay, shrineDraftOverlay, tryInteract } from './shrines.js';
import { WAVE_EVENTS } from './combat.js';
import { BOSS_KITS } from './data/bosses.js';   // boss display names for the top bar (leaf module — no cycle)
import { characterPanel } from './progression.js';   // Character Panel overlay (relics + account + stats)
import { leaderboardMenu, pauseOverlay, shopMenu } from './ui.js';
import { journalMenu, menu, roundRect } from './screens.js';
import { talentMenu } from './talents.js';
import { abbr, drawMinimap, drawPickupArrows, drawStatsPanel, fmtTime, gunCadence, gunFloor, hexA, hudBottomY, paperDps } from './render.js';
// ============================ TOUCH HUD (0.18) ============================
// On-screen controls, drawn only once a real touch confirms the device. The joystick ghost echoes
// the active touch; the right-side buttons are claimed by the touch router BEFORE aim, so a thumb
// on DASH never fires the gun. touchBtns is rebuilt every frame — buttons only exist while drawn.
export function interactNearby() {   // mirror of tryInteract's reach — lights the E button when something's in range
  for (const p of S.pickups) if (!p.got && p.kind === 'weapon' && Math.hypot(p.x - player.x, p.y - player.y) < 96) return true;
  for (const s of S.shrines) if (!s.taken && Math.hypot(s.x - player.x, s.y - player.y) < player.r + 72) return true;
  return false;
}
export function drawTouchControls() {
  S.touchBtns = [];
  if (!S.isTouch || !S.started || S.dead) return;
  // hide the joystick/buttons while any modal overlay is open — they'd clutter it and steal taps from cards
  if (S.paused || S.shopOpen || S.showJournal || S.showTalents || S.showCharacter || S.bloodConfirm || S.pandoraConfirm || S.bossReward || S.levelUp || S.shrineDraft) return;
  ctx.save();
  // joystick ghost — resting hint bottom-left, live ring + thumb while steering
  const live = joy.id !== null;
  const jx = live ? joy.ox : 92, jy = live ? joy.oy : S.H - 100;
  ctx.globalAlpha = live ? 0.45 : 0.15;
  ctx.strokeStyle = '#9fb8ff'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(jx, jy, JOY_SAT, 0, 6.2832); ctx.stroke();
  ctx.fillStyle = '#9fb8ff'; ctx.globalAlpha = live ? 0.5 : 0.18;
  ctx.beginPath(); ctx.arc(jx + joy.dx, jy + joy.dy, 22, 0, 6.2832); ctx.fill();
  // buttons: DASH (with recharge arc) above-left of the corner, E / interact above it, pause top-right
  const canDash = player.dashStock > 0;
  const dashFrac = player.dashStock >= S.DASH_CHARGES ? 1 : 1 - player.dashRe / S.DASH_RECHARGE;
  const btns = [
    { x: S.W - 70, y: S.H - 84, r: 36, label: '⤜', font: 26, action: tryDash, lit: canDash, frac: dashFrac },
    { x: S.W - 70, y: S.H - 178, r: 28, label: 'E', font: 20, action: tryInteract, lit: interactNearby() },
    { x: S.W - 32, y: 26, r: 18, label: '⏸', font: 14, action: () => { if (!S.shopOpen) S.paused = !S.paused; }, lit: false },
    { x: S.W - 32, y: 70, r: 18, label: '✦', font: 16, action: () => { S.showTalents = true; S.talentScroll = 0; }, lit: (S.talentPts || 0) > 0, pip: S.talentPts || 0 },   // talents (glows when points wait)
    { x: S.W - 32, y: 114, r: 18, label: '☷', font: 15, action: () => { S.showCharacter = true; S.charScroll = 0; S.charPickSlot = null; }, lit: (S.pendingAccountLevels || 0) > 0 },   // character panel (glows on a fresh level)
  ];
  for (const b of btns) {
    ctx.globalAlpha = 1;
    ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, 6.2832);
    ctx.fillStyle = b.lit ? 'rgba(127,182,255,.30)' : 'rgba(20,28,52,.45)'; ctx.fill();
    ctx.strokeStyle = b.lit ? 'rgba(159,216,255,.9)' : 'rgba(255,255,255,.22)'; ctx.lineWidth = 2; ctx.stroke();
    if (b.frac !== undefined && b.frac < 1) {   // dash recharge sweep
      ctx.strokeStyle = 'rgba(127,182,255,.8)'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(b.x, b.y, b.r - 4, -Math.PI / 2, -Math.PI / 2 + b.frac * 6.2832); ctx.stroke();
    }
    ctx.fillStyle = b.lit ? '#eaf4ff' : '#8a97c4'; ctx.font = 'bold ' + b.font + 'px system-ui';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(b.label, b.x, b.y + 1);
    if (b.pip) {   // unspent-talent-point badge
      ctx.fillStyle = '#ffd24a'; ctx.beginPath(); ctx.arc(b.x + b.r - 4, b.y - b.r + 4, 8, 0, 6.2832); ctx.fill();
      ctx.fillStyle = '#1a1428'; ctx.font = 'bold 10px system-ui'; ctx.fillText(b.pip > 9 ? '9+' : b.pip + '', b.x + b.r - 4, b.y - b.r + 5);
    }
    S.touchBtns.push(b);
  }
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}
export function drawDmgMeter() {
  if (!S.save.settings.dmgMeter) return;
  const rows = Object.keys(S.dmgBySrc)
    .map(k => ({ k, v: S.dmgBySrc[k], m: srcMeta(k) }))
    .filter(r => r.v > 0)
    .sort((a, b) => b.v - a.v);
  if (!rows.length) return;
  const total = rows.reduce((s, r) => s + r.v, 0), max = rows[0].v;
  const show = rows.slice(0, 5);                          // keep it glanceable: only the top few
  const rowH = 15, padX = 9, w = 190, titleH = 17;
  const h = titleH + show.length * rowH + 6;
  const x = 24, y = hudBottomY(w) - h;   // ducks under the XP bar / joystick zone (0.19 UI pass)
  ctx.save();
  ctx.fillStyle = S.isTouch ? 'rgba(8,12,26,.42)' : 'rgba(8,12,26,.6)'; roundRect(x, y, w, h, 8); ctx.fill();   // see-through on touch — it sits over the play field
  ctx.strokeStyle = 'rgba(255,255,255,.08)'; ctx.lineWidth = 1; ctx.stroke();
  ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
  ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 10px system-ui';
  ctx.fillText('⚔ DAMAGE', x + padX, y + titleH / 2 + 2);
  const dps = total / Math.max(1, S.survival / 60);          // survival is frames @60fps → damage-per-second so far
  ctx.textAlign = 'right'; ctx.fillStyle = '#8a97c4';
  ctx.fillText(abbr(total) + '  ·  ' + abbr(dps) + '/s', x + w - padX, y + titleH / 2 + 2);
  show.forEach((r, i) => {
    const ry = y + titleH + i * rowH;
    const col = r.m ? r.m.col : '#9fb0e0', name = r.m ? r.m.name : r.k;
    ctx.fillStyle = hexA(col, 0.22); roundRect(x + 4, ry + 1, Math.max(3, (w - 8) * (r.v / max)), rowH - 2, 3); ctx.fill();
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillStyle = hexA(col, 1); ctx.font = 'bold 10px system-ui';
    ctx.fillText(name, x + padX, ry + rowH / 2 + 1);
    ctx.textAlign = 'right'; ctx.fillStyle = '#e6ecff';
    ctx.fillText(abbr(r.v), x + w - padX, ry + rowH / 2 + 1);
  });
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}
// big top-of-screen boss HP bar (replaces the small floating one): name + elite affix + 3 phase pips,
// fill SEGMENTED at the 0.55/0.25 phase walls so you read the transitions coming. Duo = a 2nd thinner bar.
const AFFIX_COL = { swift: '#7fffd0', volley: '#ffb347', echo: '#ff6ec7' };
function drawBossBar() {
  const bosses = S.enemies.filter(e => !e.dead && e.kind === 'boss');
  if (!bosses.length) return;
  const narrow = S.isTouch, bw = Math.min(narrow ? 560 : 640, S.W * (narrow ? 0.78 : 0.7));
  const bx = (S.W - bw) / 2, by = narrow ? 56 : 32;
  ctx.save();
  for (let i = 0; i < Math.min(bosses.length, 2); i++) {
    const b = bosses[i], h = i === 0 ? 13 : 9, yy = by + i * 19;
    const frac = Math.max(0, Math.min(1, b.hp / b.maxHp));
    ctx.fillStyle = 'rgba(0,0,0,.5)'; roundRect(bx, yy, bw, h, 4); ctx.fill();
    ctx.fillStyle = AFFIX_COL[b.affixB] || '#ff8a3a'; roundRect(bx, yy, bw * frac, h, 4); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.3)'; ctx.lineWidth = 1;   // phase walls (where the boss escalates)
    for (const t of [0.55, 0.25]) { const tx = bx + bw * t; ctx.beginPath(); ctx.moveTo(tx, yy); ctx.lineTo(tx, yy + h); ctx.stroke(); }
    ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1.4; roundRect(bx, yy, bw, h, 4); ctx.stroke();
    if (i === 0) {
      ctx.textBaseline = 'bottom';
      ctx.textAlign = 'left'; ctx.fillStyle = AFFIX_COL[b.affixB] || '#ffd66e'; ctx.font = 'bold ' + (narrow ? 13 : 16) + 'px system-ui';
      ctx.fillText((BOSS_KITS[b.btype] && BOSS_KITS[b.btype].name || 'BOSS') + (b.affixB ? '  ⟦' + b.affixB.toUpperCase() + '⟧' : ''), bx, yy - 4);
      for (let p = 0; p < 3; p++) {   // 3 phase pips, lit up to the current phase; the current one pulses
        const pr = 4, pcx = bx + bw - 6 - p * 14, lit = p <= b.phase;
        ctx.globalAlpha = lit ? (p === b.phase ? 0.55 + 0.4 * Math.sin(S.frame * 0.2) : 1) : 0.25;
        ctx.fillStyle = lit ? '#ff8a3a' : '#6b7290';
        ctx.beginPath(); ctx.arc(pcx, yy - 7, pr, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      }
    }
  }
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}
// arena-seal intro beat: a centered "VERSUS / <name>" card that fades over its lifetime
function drawBossIntro() {
  if (!S.bossIntro) return;
  const a = Math.min(1, S.bossIntro.t / (S.bossIntro.max * 0.55));
  ctx.save();
  ctx.globalAlpha = a; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const cy = S.H * 0.32;
  ctx.fillStyle = '#ff5d6e'; ctx.font = 'bold ' + (S.isTouch ? 20 : 28) + 'px system-ui';
  ctx.fillText('⚔ VERSUS', S.W / 2, cy - 24);
  ctx.fillStyle = '#ffd66e'; ctx.font = 'bold ' + (S.isTouch ? 28 : 42) + 'px system-ui';
  ctx.fillText(S.bossIntro.name, S.W / 2, cy + 16);
  ctx.restore();
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}
export function hud() {
  if (S.started && !S.dead) {
    drawPickupArrows();
    drawMinimap();
    drawDmgMeter();
    drawStatsPanel();
    drawTouchControls();
    drawBossBar();
    drawBossIntro();
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 30px system-ui';
    ctx.fillText(fmtTime(S.survival), 24, 22);
    if (DEV) { ctx.fillStyle = '#ffd24a'; ctx.font = 'bold 11px system-ui'; ctx.fillText('🔧 DEV' + (S.devGod ? ' · GOD' : ''), 24, 56); }   // cheated runs are visibly cheated
    ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 20px system-ui';
    ctx.fillText(`🪙 ${S.coinsRun}`, 26, 60);

    // health bar — narrower on touch so it (and the fervor bar) clear the right-side info cluster
    const hw = S.isTouch ? Math.min(150, S.W * 0.4) : 220, hh = 16, hx = 24, hy = 92;
    ctx.fillStyle = 'rgba(0,0,0,.4)'; roundRect(hx, hy, hw, hh, 8); ctx.fill();
    const frac = Math.max(0, player.hp) / S.MAX_HP;
    ctx.fillStyle = frac > 0.5 ? '#5fdc7a' : frac > 0.25 ? '#ffd66e' : '#ff5d6e';
    roundRect(hx, hy, hw * frac, hh, 8); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 11px system-ui';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(Math.ceil(Math.max(0, player.hp)) + ' / ' + S.MAX_HP, hx + hw / 2, hy + hh / 2 + 1);
    ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    // shield pips to the right of the bar — empty pips brighten as their own recharge timer fills
    const shCds = [...player.shieldCds].sort((a, b) => a - b);
    for (let i = 0; i < S.MAX_SHIELD; i++) {
      if (i < player.shield) ctx.fillStyle = '#7fb6ff';
      else {
        const cd = shCds[i - player.shield];
        const p = cd !== undefined ? Math.max(0, 1 - cd / S.SHIELD_RECHARGE) : 0;
        ctx.fillStyle = `rgba(127,182,255,${(0.22 + 0.5 * p).toFixed(2)})`;
      }
      // touch: pips tuck UNDER the bar (the right side is needed for the info cluster); desktop: to the right
      ctx.beginPath(); ctx.arc(S.isTouch ? hx + 6 + i * 16 : hx + hw + 16 + i * 16, S.isTouch ? hy + hh + 11 : hy + hh / 2, 5, 0, Math.PI * 2); ctx.fill();
    }

    // dash charges (Space) — pips below-right of the health bar. Skipped on touch: the on-screen DASH
    // button (with its own recharge arc) already shows this, and these pips collide with the info cluster.
    if (!S.isTouch) {
      const dlx = hx + hw + 16, dly = hy + hh + 2;
      for (let i = 0; i < S.DASH_CHARGES; i++) {
        const ready = i < player.dashStock;
        ctx.fillStyle = ready ? '#8ffcff' : 'rgba(143,252,255,.22)';
        roundRect(dlx + i * 30, dly, 24, 6, 3); ctx.fill();
        if (!ready && i === player.dashStock) {
          const prog = 1 - player.dashRe / S.DASH_RECHARGE;
          ctx.fillStyle = '#8ffcff'; roundRect(dlx + i * 30, dly, 24 * Math.max(0, prog), 6, 3); ctx.fill();
        }
      }
      ctx.fillStyle = '#6f7ba6'; ctx.font = '10px system-ui'; ctx.textBaseline = 'top';
      ctx.fillText('⤜ DASH', dlx + S.DASH_CHARGES * 30 + 4, dly - 2);
    }

    // fervor / momentum meter under the health bar (on touch it sits below the tucked-in shield pips)
    const fy = hy + hh + (S.isTouch && S.MAX_SHIELD > 0 ? 22 : 4);
    const fcol = S.fervorTier >= 4 ? '#ff5d6e' : S.fervorTier >= 3 ? '#ff944d' : S.fervorTier >= 2 ? '#ffd66e' : '#7dffb0';
    ctx.fillStyle = 'rgba(0,0,0,.4)'; roundRect(hx, fy, hw, 7, 4); ctx.fill();
    ctx.fillStyle = fcol; roundRect(hx, fy, hw * Math.min(1, S.fervor / S.FERVOR_MAX), 7, 4); ctx.fill();
    ctx.fillStyle = S.fervorTier >= 1 ? fcol : '#6f7ba6'; ctx.font = 'bold 10px system-ui';
    ctx.fillText(S.fervorTier >= 4 ? '🔥 OVERDRIVE' : 'FERVOR  +' + Math.round(S.fervorTier * 13) + '% dmg', hx, fy + 9);

    // XP / level — a slim bar at bottom-center; each level lets you draft a permanent stat
    {
      const xw = Math.min(380, S.W * 0.36), xh = 8, xx = (S.W - xw) / 2, xy = S.H - 30;
      ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      // unspent TALENT POINTS — a pulsing pill above the level label so PC players (no touch ✦ button) see it
      if ((S.talentPts || 0) > 0) {
        const lbl = '✦ ' + S.talentPts + ' TALENT POINT' + (S.talentPts > 1 ? 'S' : '') + (S.isTouch ? '  ▸ tap ✦' : '  ▸ press N');
        ctx.font = 'bold 12px system-ui';
        const pw2 = ctx.measureText(lbl).width + 22, ph2 = 22, pyT = xy - 26;
        ctx.globalAlpha = 0.5 + 0.5 * Math.sin(S.frame * 0.1);
        ctx.fillStyle = 'rgba(255,210,74,.20)'; roundRect((S.W - pw2) / 2, pyT - 16, pw2, ph2, 7); ctx.fill();
        ctx.strokeStyle = '#ffd24a'; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#ffd24a'; ctx.fillText(lbl, S.W / 2, pyT - 1);
      }
      ctx.fillStyle = '#bdf3d6'; ctx.font = 'bold 12px system-ui';
      ctx.fillText('✦ LEVEL ' + S.level, S.W / 2, xy - 6);
      ctx.fillStyle = 'rgba(0,0,0,.45)'; roundRect(xx, xy, xw, xh, 4); ctx.fill();
      ctx.fillStyle = '#7dffb0'; roundRect(xx, xy, xw * Math.max(0, Math.min(1, S.xp / S.xpNext)), xh, 4); ctx.fill();
      if (S.pendingLevels > 0) { ctx.fillStyle = '#ffd66e'; ctx.font = 'bold 11px system-ui'; ctx.fillText('LEVEL UP!', S.W / 2, xy + xh + 13); }
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    }

    // wave counter + Threat level, top-right. On touch, right-anchor clear of the pause button in the corner.
    const rx = S.isTouch ? S.W - 54 : S.W - 24;
    ctx.textAlign = 'right';
    if (S.waveNum > 0) {
      ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 18px system-ui';
      ctx.fillText('Wave ' + S.waveNum, rx, 24);
    }
    ctx.fillStyle = S.threat >= 4 ? '#ff5d6e' : S.threat >= 2.5 ? '#ff944d' : S.threat >= 1.6 ? '#ffd66e' : '#9be8c0';
    ctx.font = 'bold 13px system-ui';
    ctx.fillText('☠ Threat ×' + S.threat.toFixed(1), rx, 44);
    // active special-wave badge — a persistent pulsing pill at top-center so you always know the twist in play
    if (S.waveEvent && !S.arena.active) {
      const ev = WAVE_EVENTS[S.waveEvent];
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.globalAlpha = 0.7 + 0.3 * Math.sin(S.frame * 0.12);
      ctx.font = 'bold 15px system-ui';
      const label = '⚠ ' + ev.name, bw = ctx.measureText(label).width + 28;
      ctx.fillStyle = hexA(ev.color, 0.16); roundRect(S.W / 2 - bw / 2, 16, bw, 26, 13); ctx.fill();
      ctx.strokeStyle = hexA(ev.color, 0.7); ctx.lineWidth = 1.4; ctx.stroke();
      ctx.fillStyle = ev.color; ctx.fillText(label, S.W / 2, 30);
      ctx.restore();
      ctx.textAlign = 'right'; ctx.textBaseline = 'top';
    }
    // live-update pill — a new build is waiting; it applies automatically on the next run
    if (S.updateReady) {
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = 'bold 11px system-ui';
      const ulabel = '⬆ UPDATE READY — applies after this run', ubw = ctx.measureText(ulabel).width + 22;
      const uy = (S.waveEvent && !S.arena.active) ? 50 : 16;   // sit below the special-wave badge when one is up
      ctx.fillStyle = 'rgba(125,255,176,.12)'; roundRect(S.W / 2 - ubw / 2, uy, ubw, 20, 10); ctx.fill();
      ctx.strokeStyle = 'rgba(125,255,176,.5)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = '#7dffb0'; ctx.fillText(ulabel, S.W / 2, uy + 11);
      ctx.restore();
      ctx.textAlign = 'right'; ctx.textBaseline = 'top';
    }
    // live DPS readout + current weapon (+evolution) + mute indicator (build clarity)
    const wpn = WEAPONS[S.weapon];
    ctx.fillStyle = '#8a97c4'; ctx.font = '12px system-ui';
    const _rate = (60 / Math.max(gunFloor(wpn), gunCadence(wpn))).toFixed(1);
    ctx.fillText('gun ~' + abbr(paperDps()) + ' dps · ' + _rate + '/s', rx, 64);   // fire RATE shown (player request: "I keep picking it and can't see it")
    ctx.fillStyle = S.EVO2 ? '#ffe9a8' : S.EVO ? '#ffd66e' : '#cdd6ff'; ctx.font = 'bold 12px system-ui';
    ctx.fillText(wpn.icon + ' ' + (S.EVO2 ? '★★ ' + S.EVO2.name : S.EVO ? '★ ' + S.EVO.name : wpn.name) + (S.save.settings.autoAim ? '  ◎ AUTO' : ''), rx, 82);   // ◎ = auto-aim engaged (T)
    let infoY = 100;
    if (!S.EVO && wpn.evo) {   // evolution progress hint — shows the destination and how close you are
      ctx.fillStyle = '#6f7ba6'; ctx.font = '11px system-ui';
      const evoN = S.items.apotheosis ? BAL.evo.apotheosisNeed : BAL.evo.need;
      ctx.fillText('⤴ ' + ARCH_INFO[wpn.evo.arch].icon + ' ' + Math.min(evoN, S.arch[wpn.evo.arch] || 0) + '/' + evoN + ' → ' + wpn.evo.name, rx, infoY);
      infoY += 16;
    } else if (S.EVO && wpn.evo2 && !S.EVO2) {   // ascension hint — the deed that unlocks stage 2
      ctx.fillStyle = '#6f7ba6'; ctx.font = '11px system-ui';
      ctx.fillText('⤴⤴ slay a boss → ' + wpn.evo2.name, rx, infoY);
      infoY += 16;
    }
    if (S.buildTag) {   // current build identity
      ctx.fillStyle = '#9be8c0'; ctx.font = 'bold 12px system-ui';
      ctx.fillText(ARCH_INFO[S.buildTag].icon + ' ' + S.buildName, rx, infoY);
      infoY += 18;
    }
    if (Audio.isMuted()) { ctx.fillStyle = '#cdd6ff'; ctx.font = 'bold 12px system-ui'; ctx.fillText('🔇 muted (M)', rx, infoY); }
    ctx.textAlign = 'left';

    // item bar — your stacking run items, count tinted by rarity. Wraps to 2 rows, then
    // collapses into "+N" so a deep run can't push the bar off-screen / into the right HUD.
    const ibX0 = 26, ibMaxX = Math.max(300, S.W - 330), ibRowH = 32, ibRows = 2;
    let ix = ibX0, iy = 142, ibHidden = 0;
    for (const it of ITEMS) {
      const n = S.items[it.key]; if (!n) continue;
      if (ix + 44 > ibMaxX) {
        if (iy >= 142 + (ibRows - 1) * ibRowH) { ibHidden++; continue; }   // out of rows — tally the rest
        ix = ibX0; iy += ibRowH;
      }
      ctx.font = '20px system-ui'; ctx.fillStyle = '#fff';
      ctx.fillText(it.icon, ix, iy);
      ctx.font = 'bold 12px system-ui'; ctx.fillStyle = TIERS[it.tier].color;
      ctx.fillText('×' + n, ix + 22, iy + 8);
      ix += 46;
    }
    if (ibHidden > 0) {
      ctx.font = 'bold 12px system-ui'; ctx.fillStyle = '#8a97c4';
      ctx.fillText('+' + ibHidden + ' (Esc)', ix, iy + 8);
      ix += 52;
    }
    if (S.equipment.length) {   // growing gear collection count
      ctx.font = '18px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText('⚙', ix, iy + 1);
      ctx.font = 'bold 12px system-ui'; ctx.fillStyle = '#cdd6ff'; ctx.fillText('×' + S.equipment.length, ix + 20, iy + 8);
    }

    // chain combo readout — flashes during a cascade. On a phone the top-right info cluster reaches
    // toward the centre, so drop the readout below it (and cap its size) instead of overlapping Wave/weapon.
    if (S.chainCount >= 4) {
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.globalAlpha = Math.min(1, S.chainTimer / 30);
      const narrow = S.W < 560;
      const sz = (narrow ? 26 : 30) + Math.min(S.chainCount, narrow ? 22 : 40);
      ctx.fillStyle = '#ffe08a'; ctx.font = `bold ${sz}px system-ui`;
      ctx.fillText(S.chainCount + '× CHAIN', S.W / 2, narrow ? 158 : 64);
      ctx.restore();
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    }

    // wave-start banner
    if (S.waveBanner) {
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.globalAlpha = Math.min(1, S.waveBanner.life);
      ctx.fillStyle = '#ff8d6e'; ctx.font = 'bold 50px system-ui';
      ctx.fillText(S.waveBanner.text, S.W / 2, S.H * 0.28);
      ctx.restore();
      ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    }
  }
  if (S.shopOpen) shopMenu();
  else if (S.paused && S.started && !S.dead) pauseOverlay();
  if ((!S.started || S.dead) && S.showLeaderboard) leaderboardMenu();   // leaderboard overlays the start/death menu
  else if (!S.started) menu(false);
  else if (S.dead) menu(true);
  if (S.bloodConfirm) bloodConfirmOverlay();   // Blood Shrine yes/no prompt, mid-run
  if (S.pandoraConfirm) pandoraConfirmOverlay();   // Pandora's Box yes/no prompt, mid-run
  if (S.bossReward) bossRewardOverlay();   // boss-kill pick-1-of-3 draft, mid-run
  if (S.shrineDraft) shrineDraftOverlay(); // item-cache buy-1-of-3 draft, mid-run
  if (S.levelUp) levelUpOverlay();         // level-up stat draft, mid-run
  if (S.showTalents) talentMenu();    // talent tree overlays the field, mid-run
  if (S.showJournal) journalMenu();   // item codex overlays everything, on a menu or mid-run
  if (S.showCharacter) characterPanel();   // relics + account + lifetime stats, on a menu or mid-run
  drawAchToasts();                  // achievement banners float above everything
  drawRelicToasts();               // relic-drop banners (under the achievement banners)
}
